import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { AiCommandPaletteProvider, useAiCommandPalette } from './context/AiCommandPaletteContext';
import SearchInput from './components/SearchInput';
import SearchResults from './components/SearchResults';
import AiResults from './components/AiResults';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import ErrorMessage from './components/ErrorMessage';
import Suggestions from './components/Suggestions';

// Import context helpers
import * as contextHelpers from './utils/contextHelpers';

// The actual command palette UI that uses the context
const CommandPaletteContent = ({ pageContext, initialAskQuery, onAskConsumed }) => {
  const { 
    isOpen, 
    onClose, 
    handleOverlayClick,
    isAskMode,
    completedMessages,
    currentMessage,
    setSelectedIndex,
    setContextFromPage,
    handleAiAsk
  } = useAiCommandPalette();

  // Set context from page when command palette opens
  const [contextReady, setContextReady] = React.useState(false);
  useEffect(() => {
    if (!isOpen) {
      setContextReady(false);
      return;
    }
    
    if (isOpen && pageContext) {
      setContextFromPage(pageContext);
      // Wait for next tick to ensure state has updated
      setTimeout(() => setContextReady(true), 0);
    } else if (isOpen && !pageContext) {
      // No page context to set, ready immediately
      setContextReady(true);
    }
  }, [isOpen, pageContext, setContextFromPage]);

  // Trigger an initial ask once when opened with a query - but only after context is ready
  const hasTriggeredInitialAskRef = React.useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasTriggeredInitialAskRef.current = false;
      return;
    }
    if (isOpen && contextReady && initialAskQuery && !hasTriggeredInitialAskRef.current) {
      hasTriggeredInitialAskRef.current = true;
      handleAiAsk(initialAskQuery);
      if (onAskConsumed) onAskConsumed();
    }
  }, [isOpen, contextReady, initialAskQuery, handleAiAsk, onAskConsumed]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 bg-gray-100/40 dark:bg-black/50 backdrop-blur-md z-50"
        onClick={handleOverlayClick}
      >
        <motion.div 
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          exit={{ y: -20 }}
          className={`container max-w-3xl mx-auto ${isAskMode ? 'mt-[10vh]' : 'mt-[20vh]'} transition-[margin-top]`}
          onMouseLeave={() => setSelectedIndex(-1)}
        >
          <div 
            className="bg-white/80 dark:bg-black/60 backdrop-blur-xl rounded-2xl border border-violet-500 dark:border-violet-600 shadow-xl shadow-violet-500/10 overflow-hidden"
            onMouseMove={(e) => {
              // Only reset selection if mouse is directly on the container but not on a result item
              // This prevents erratic behavior when moving between options
              if (e.target === e.currentTarget) {
                setSelectedIndex(-1);
              }
            }}
          >
            {/* Input Section */}
            <SearchInput />

            {/* <Suggestions /> */}

            {/* Results Section */}
            <div className={`overflow-y-auto ${isAskMode ? 'max-h-[65vh]' : 'max-h-[40vh]'}`}>
              <ErrorMessage />
              
              {/* Show AI Results or Search Results based on mode */}
              {isAskMode || currentMessage.userQuery || completedMessages.length > 0 ? (
                <AiResults />
              ) : (
                <SearchResults />
              )}
            </div>
          </div>

          {/* Suggestions below the main palette */}
          <Suggestions />
          
          {/* Keyboard shortcuts help box */}
          {/* <KeyboardShortcuts /> */}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// The main component that provides the context
const AiCommandPalette = ({ isOpen, onClose, pageContext, setPageContext, initialAskQuery, onAskConsumed }) => {
  return (
    <AiCommandPaletteProvider isOpen={isOpen} onClose={onClose} setPageContext={setPageContext}>
      <CommandPaletteContent pageContext={pageContext} initialAskQuery={initialAskQuery} onAskConsumed={onAskConsumed} />
    </AiCommandPaletteProvider>
  );
};

// Export context helpers alongside the component
export { contextHelpers };

export default AiCommandPalette; 